import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  IconButton,
} from "@mui/material";
import { ExpandMore, MoreVert, CalendarToday } from "@mui/icons-material";

export default function BankCard({
  bankName,
  relationship,
  relationshipColor,
  badgeText,
  badgeColor,
  totalBalance,
  creditCards,
  accountTypes,
  utilization,
  accountDetails,
  lastActivity,
}) {
  return (
    <Card
      variant="outlined"
      sx={{ borderColor: relationshipColor, borderWidth: 2, borderRadius: 3, width: '100%', height: '100%' }}
    >
      <CardContent>
        {/* Header */}
        <Grid container justifyContent="space-between" alignItems="center" mb={1}>
          <Grid item>
            <Typography variant="h6" fontWeight="bold">
              {bankName}
            </Typography>
            <Chip
              label={badgeText}
              size="small"
              sx={{ mt: 0.5, backgroundColor: badgeColor, fontSize: "0.75rem" }}
            />
          </Grid>
          <Grid item>
            <IconButton size="small">
              <MoreVert />
            </IconButton>
          </Grid>
        </Grid>

        {/* Summary */}
        <Grid container spacing={2} mb={1}>
          <Grid item xs={6}>
            <Typography color="text.secondary" variant="body2">
              Total Balance
            </Typography>
            <Typography fontWeight="bold">{totalBalance}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography color="text.secondary" variant="body2">
              Credit Cards
            </Typography>
            <Typography fontWeight="bold">{creditCards}</Typography>
          </Grid>
        </Grid>

        {/* Account Types */}
        <Box display="flex" gap={1} mb={1} flexWrap="wrap">
          {accountTypes.map((type, idx) => (
            <Chip
              key={idx}
              label={`${type.count} ${type.label}`}
              size="small"
              variant="outlined"
            />
          ))}
        </Box>

        {/* Utilization */}
        <Typography color="text.secondary" variant="body2">
          Credit Utilization {utilization.percent} of {utilization.value}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={parseInt(utilization.percent)}
          sx={{ borderRadius: 1, height: 8, mb: 2 }}
        />

        {/* Accordion - improved row styling to look like compact cards when expanded */}
        <Accordion disableGutters sx={{ boxShadow: 'none' }}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
          >
            <Typography variant="body2" fontWeight={600}>
              View Account Details ({accountDetails.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1 }}>
            <Grid container spacing={2}  wrap="nowrap" direction="column">
              {accountDetails.map((acc, idx) => {
                // normalize utilization value (handles strings like "16%" or numbers)
                const raw = acc.utilized || acc.progressValue || acc.utilization || '0';
                const percent = Math.min(100, Math.max(0, parseFloat(String(raw).replace('%', '')) || 0));
                return (
                  <Grid key={idx} item xs={12}>
                    <Box
                      sx={{
                        p: 1.25,
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: '0 1px 6px rgba(16,24,40,0.04)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <Grid container alignItems="center" justifyContent="space-between">
                        <Grid item>
                          <Typography fontWeight={700}>{acc.name}</Typography>
                        </Grid>
                        <Grid item>
                          <Typography fontWeight={700} textAlign="right">{acc.owed}</Typography>
                        </Grid>
                      </Grid>

                      <Grid container alignItems="center" justifyContent="space-between">
                        <Grid item xs={8}>
                          <Typography variant="body2" color="text.secondary">
                            {acc.masked} {acc.limit ? `• ${acc.limit}` : ''}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ float: 'right' }}>
                            {acc.apr ? `${acc.apr} APR` : ''}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Box>
                        <LinearProgress
                          variant="determinate"
                          value={percent}
                          sx={{ height: 6, borderRadius: 1, mb: 0.5 }}
                        />
                        <Grid container justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary">
                            {percent}% utilized
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {acc.limit ? `${acc.limit}` : ''}
                          </Typography>
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Last Activity */}
        <Box display="flex" alignItems="center" gap={1} mt={2}>
          <CalendarToday fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            Last Activity {lastActivity}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
